/**
 * Shared utility for opening or downloading document URLs.
 * Handles data URLs (base64), S3 keys, and http(s) URLs.
 */

import { isS3Key, resolveFileUrl } from "./fileUpload";

export function isDataUrl(url: string): boolean {
	return url?.startsWith("data:") ?? false;
}

/**
 * Opens a document URL for viewing (new tab).
 * - Data URLs: creates blob URL and opens (avoids browser blocking of data: in new tab)
 * - S3 keys: resolves to presigned URL first, then opens
 * - http(s) URLs: opens in new tab
 *
 * @param url - The document URL (data URL, S3 key, or http(s))
 */
export async function openDocumentUrl(url: string): Promise<void> {
	if (!url || url.trim() === "" || url === "#" || url === "undefined") {
		return;
	}

	// S3 keys need resolution to a presigned URL before opening
	if (isS3Key(url)) {
		const resolved = await resolveFileUrl(url);
		if (resolved) {
			window.open(resolved, "_blank", "noopener,noreferrer");
		}
		return;
	}

	if (isDataUrl(url)) {
		// Data URLs in window.open() are often blocked; create blob URL first
		fetch(url)
			.then((r) => r.blob())
			.then((blob) => {
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl, "_blank", "noopener,noreferrer");
				// Revoke after a delay to allow the new tab to load
				setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
			})
			.catch(() => {
				// Fallback: try direct open (may work in some browsers)
				window.open(url, "_blank", "noopener,noreferrer");
			});
	} else {
		let fullUrl = url.trim();
		if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
			fullUrl = `https://${fullUrl}`;
		}
		window.open(fullUrl, "_blank", "noopener,noreferrer");
	}
}

/**
 * Downloads a document URL.
 * - Data URLs: creates a temporary anchor and triggers download
 * - S3 keys: resolves to presigned URL first, then opens (browser prompts save)
 * - http(s) URLs: opens in new tab (browser may prompt save)
 *
 * @param url - The document URL (data URL, S3 key, or http(s))
 * @param downloadName - Optional filename for download (default: "document")
 */
export async function openOrDownloadUrl(
	url: string,
	downloadName?: string,
): Promise<void> {
	if (!url || url.trim() === "" || url === "#" || url === "undefined") {
		return;
	}

	// S3 keys need resolution to a presigned URL before downloading
	if (isS3Key(url)) {
		const resolved = await resolveFileUrl(url);
		if (resolved) {
			window.open(resolved, "_blank", "noopener,noreferrer");
		}
		return;
	}

	if (isDataUrl(url)) {
		const link = document.createElement("a");
		link.href = url;
		link.download = downloadName ?? "document";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} else {
		let fullUrl = url.trim();
		if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
			fullUrl = `https://${fullUrl}`;
		}
		window.open(fullUrl, "_blank", "noopener,noreferrer");
	}
}
